import classNames from "classnames";
import type {
  GetServerSideProps,
  InferGetServerSidePropsType,
  NextPage,
} from "next";
import Head from "next/head";
import React, { useState } from "react";
import { v4 } from "uuid";
import Advertisement from "../../../../components/Advertisement";
import Container from "../../../../components/Container";
import Footer from "../../../../components/Footer";
import PrimaryGrid from "../../../../components/Grid/PrimaryGrid";
import SecondaryGrid from "../../../../components/Grid/SecondaryGrid";
import { Media } from "../../../../components/Media";
import Navigation from "../../../../components/Navigation";
import MainFeedDesktop from "../../../../components/pages/categoryNews/MainFeedDesktop";
import MainFeedMobile from "../../../../components/pages/categoryNews/MainFeedMobile";
import SecondaryFeed from "../../../../components/pages/categoryNews/SecondaryFeed";
import StickySidebar from "../../../../components/StickySidebar";
import AdsIds, { AdsIdsMobileCollection } from "../../../../data/ads";
import TransferWindowIds from '../../../../data/transferWindow';
import { listByIds } from "../../../../services/api/ads";
import { getManager, getManagerNews } from "../../../../services/api/manager";
import { listTransferWindowById } from '../../../../services/api/transferWindow';
import { API } from "../../../../services/api/types";
import addTrailingS from "../../../../utils/addTrailingS";
import { AdsContext } from "../../../../utils/AdsContext";
import checkImageWithFallback from "../../../../utils/checkImageWithFallback";
import { DeviceTypeProvider } from "../../../../utils/deviceTypeContext";
import { createServerSidePropsError } from "../../../../utils/errorHandling/createServerSidePropsError";
import { logError } from "../../../../utils/errorHandling/logError";
import { DeviceType, getDeviceType } from "../../../../utils/getDeviceType";
import slugify from "../../../../utils/slugify";
import useCanonicalUrl from '../../../../hooks/useCanonicalUrl';

type GetServerSidePropsParams = {
  slug: string;
  id: string;
};

const limit = 20;

export const getServerSideProps: GetServerSideProps<
  {
    news?: API.Article[];
    manager?: API.Manager;
    profileImage?: string;
    ads?: API.Ad[];
    deviceType?: DeviceType;
    transferWindow?: API.TransferWindow
  },
  GetServerSidePropsParams
> = async (context) => {
  try {
    const { slug, id } = context.params!;
    const manager = await getManager(id);

    if (!manager.data?.id) {
      context.res.statusCode = 404;
      
      return createServerSidePropsError(404)
    }

    const news = await getManagerNews(id, { limit });

    const ads = await listByIds([
      AdsIds.panorama_1,
      AdsIds.mobile_1,
      AdsIds.tws_1,
      AdsIds.tws_2,
      AdsIds.desktop_vi,
      AdsIds.mobile_vi,
      AdsIds.dynamic_desktop,
      AdsIds.dynamic_mobile,
      ...AdsIdsMobileCollection,
    ]);

    const transferWindow = await listTransferWindowById(TransferWindowIds.swedish);

    const profileImage = await checkImageWithFallback(
      manager.data.imageLarge,
      "/profile-fallback.jpg"
    );

    const correctSlug = slugify(manager.data.name);
    if (correctSlug !== slug)
      return {
        redirect: {
          destination: `/tranare/${correctSlug}/${id}/nyheter`,
          permanent: true,
        },
      };

    return {
      props: {
        news: news.data,
        manager: manager.data,
        profileImage,
        ads: ads.data,
        deviceType: getDeviceType(context),
        transferWindow: transferWindow.data
      },
    };
  } catch (error: FixMeLater) {
    const errorId = v4();
    logError(error, errorId);
    return createServerSidePropsError(500, errorId);
  }
};

const ManagerNewsScreen: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const { manager, profileImage } = props;
  const [news, setNews] = useState(props.news);
  const [newsOffset, setNewsOffset] = useState(0);
  const [hasReachedEnd, sethasReachedEnd] = useState(
    props.news!.length < limit
  );
  const nrOfArticles = 20;
  const profileLink = `/tranare/${slugify(manager!.name)}/${manager!.id}`;
  const profileLinkText = `Visa ${addTrailingS(manager!.name)} profil`;

  const onReachEnd = async () => {
    if (hasReachedEnd) return;
    const response = (
      await getManagerNews(String(manager!.id), {
        limit: nrOfArticles,
        offset: newsOffset,
      })
    ).data as API.Article[];

    if (response.length < 1) {
      sethasReachedEnd(true);
    }
    setNewsOffset(newsOffset + nrOfArticles);
    setNews([...news!, ...response] as FixMeLater);
  };

  const canonicalUrl = useCanonicalUrl();

  return (
    <AdsContext.Provider value={props.ads!}>
      <DeviceTypeProvider value={props.deviceType!}>
        <>
          <Head>
            <title>Nyheter om {props.manager!.name} - Fotbolltransfers</title>
            <meta
              name="description"
              content={`Fotbolltransfers ger dig de senaste nyheterna om ${
                props.manager!.name
              }. Håll dig uppdaterad om ${
                props.manager!.name
              } och nyheter inom fotbollsvärlden här!`}
            />
            <link rel="canonical" href={canonicalUrl} />
          </Head>

          <Container>
            <Navigation />
          </Container>

          <Container horizontalMargin>
            <PrimaryGrid
              top={
                <>
                  <Media greaterThanOrEqual="panorama">
                    <Advertisement id={AdsIds.panorama_1} />
                  </Media>
                  <Media lessThan="panorama">
                    <Advertisement id={AdsIds.mobile_1} />
                  </Media>
                </>
              }
              right={
                <StickySidebar>
                  <Advertisement id={AdsIds.tws_1} />
                  <div className={classNames("mb-5")} />
                  <Advertisement id={AdsIds.tws_2} />
                </StickySidebar>
              }
              main={
                <SecondaryGrid
                  left={
                    <>
                      <Media greaterThanOrEqual="md">
                        <MainFeedDesktop
                          news={news!}
                          sectionHeader={`Nyheter om ${manager!.name}`}
                          onReachEnd={onReachEnd}
                          hasReachedEnd={hasReachedEnd}
                          rssFeed={`/rss/tranare/${manager!.id}`}
                        />
                      </Media>
                      <Media lessThan="md">
                        <MainFeedMobile
                          news={news!}
                          sectionHeader={`Nyheter om ${manager!.name}`}
                          sectionLabel="Tränarprofil"
                          linkText={profileLinkText}
                          linkUrl={profileLink}
                          profileImage={profileImage}
                          onReachEnd={onReachEnd}
                          hasReachedEnd={hasReachedEnd}
                          rssFeed={`/rss/tranare/${manager!.id}`}
                        />
                      </Media>
                    </>
                  }
                  right={
                    <SecondaryFeed
                      linkText={profileLinkText}
                      linkUrl={profileLink}
                      profileImage={profileImage}
                      sectionLabel="Tränarprofil"
                      transferWindow={props.transferWindow}
                    />
                  }
                />
              }
            />
            <Footer />
          </Container>
        </>
      </DeviceTypeProvider>
    </AdsContext.Provider>
  );
};

export default ManagerNewsScreen;
